import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Alert, Stack, Stepper, Step, StepLabel, Typography, LinearProgress, Box } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCompleteOrderMutation, useUpdateOrderStatusMutation } from "@/features/organizations/api/ordersApi";
import useSnackbar from "@/hooks/useSnackbar";
import { Order, OrderStatus, PaymentMethodsType } from "@/types";
import useOrganizations from "@/features/organizations/useOrganizations";
import { getColor } from '@/pages/organization-orders/utils/performanceColors';
import { green, grey } from "@mui/material/colors";

const calculateMinutesBetweenDates = (start: any, end: any): number | null => {
    if (!start || !end) return null;

    const startDate = start?.toDate ? start.toDate() : new Date(start);
    const endDate = end?.toDate ? end.toDate() : new Date(end);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;

    const diffInMs = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.round(diffInMs / 60000) || 1;
};

interface OrderStagesProps {
    order: Order;
}

const OrderStages = ({ order }: OrderStagesProps) => {
    const [openSnackbar] = useSnackbar();
    const { currentOrganization } = useOrganizations();
    const { organizationId } = useParams<{ organizationId: string }>();
    const [updateOrderStatus, isUpdatingStatusMutation] = useUpdateOrderStatusMutation();
    const [completeOrder] = useCompleteOrderMutation();

    const stripeEnabled = Boolean(currentOrganization?.settings?.paymentMethods?.stripeEnabled);
    const isCardPayment = order?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD;
    const isOrderPaid = order?.paymentDetails?.status === "paid" || Boolean(order?.paidAt);
    const isStripeOrder =
        (stripeEnabled && isCardPayment) ||
        isOrderPaid ||
        Boolean(order?.paymentDetails?.stripeSessionId) ||
        order?.paymentDetails?.status === "pending" ||
        order?.status === OrderStatus.PENDING_PAYMENT;
    const requiresStripePayment = isStripeOrder && !isOrderPaid;

    const effectiveStatus = useMemo(() => {
        if (requiresStripePayment && order?.status === OrderStatus.PREPARING) {
            return OrderStatus.PENDING_PAYMENT;
        }
        return order?.status;
    }, [requiresStripePayment, order?.status]);

    const preparationStartTime = isStripeOrder ? (order?.paidAt || order?.approvedAt) : order?.approvedAt;

    const steps = useMemo(() => {
        return [
            {
                id: OrderStatus.REVIEWING,
                order: 0,
                flows: ["delivery", "pick-up", "dine-in"],
                title: "Revisando",
                tagline: "¿Está todo bien con el pedido?",
                btnLabel: requiresStripePayment ? "Aprobar y solicitar pago" : "Preparar",
                estimatedTime: Number(currentOrganization?.settings?.operationTiming?.review),
                time: calculateMinutesBetweenDates(order?.orderedAt, order?.approvedAt),
                color: getColor(order?.orderedAt, order?.approvedAt, currentOrganization?.settings?.operationTiming?.review),
                btnAction: async () =>
                    await updateOrderStatus({
                        organizationId: organizationId as string,
                        orderId: order?.id,
                        status: requiresStripePayment ? OrderStatus.PENDING_PAYMENT : OrderStatus.PREPARING,
                    }),
            },
            ...(isStripeOrder
                ? [
                    {
                        id: OrderStatus.PENDING_PAYMENT,
                        order: 0.5,
                        flows: ["delivery", "pick-up", "dine-in"],
                        title: isOrderPaid ? "Pagado" : "Esperando Pago",
                        tagline: "Esperando que el cliente complete el pago con tarjeta en Stripe...",
                        btnLabel: "",
                        estimatedTime: 0,
                        time: calculateMinutesBetweenDates(order?.approvedAt, order?.paidAt),
                        color: isOrderPaid || order?.status === OrderStatus.COMPLETED ? green[500] : grey[400],
                    },
                ]
                : []),
            {
                id: OrderStatus.PREPARING,
                order: 1,
                flows: ["delivery", "pick-up", "dine-in"],
                title: "Preparando",
                tagline: "¿Está listo el pedido?",
                btnLabel: order?.flow === "delivery" ? "Enviar" : "Entregar",
                estimatedTime: Number(currentOrganization?.settings?.operationTiming?.preparation),
                time: calculateMinutesBetweenDates(preparationStartTime, order?.sendingAt),
                color: getColor(
                    preparationStartTime,
                    order?.flow === 'delivery' ? order?.sendingAt : order?.waitingAt,
                    currentOrganization?.settings?.operationTiming?.preparation
                ),
                btnAction: async () =>
                    await updateOrderStatus({
                        organizationId: organizationId as string,
                        orderId: order?.id,
                        status: order?.flow === "delivery" ? OrderStatus.SENDING : OrderStatus.WAITING,
                    }),
            },
            {
                id: OrderStatus.SENDING,
                order: 2,
                flows: ["delivery"],
                title: "Enviando",
                tagline: "¿Completaste el pedido?",
                btnLabel: "Completar",
                estimatedTime: Number(currentOrganization?.settings?.operationTiming?.shipping),
                time: calculateMinutesBetweenDates(order?.sendingAt, order?.completedAt),
                color: getColor(order?.sendingAt, order?.completedAt, currentOrganization?.settings?.operationTiming?.shipping),
                btnAction: async () => {
                    await completeOrder({
                        order: order as any,
                        organizationId: organizationId as string,
                        orderId: order?.id,
                    });
                },
            },
            {
                id: OrderStatus.WAITING,
                order: 2,
                flows: ["pick-up", "dine-in"],
                title: "Entregando",
                tagline: "Entregaste el pedido?",
                btnLabel: "Completar",
                estimatedTime: Number(currentOrganization?.settings?.operationTiming?.shipping),
                time: calculateMinutesBetweenDates(order?.sendingAt, order?.completedAt),
                color: getColor(order?.sendingAt, order?.completedAt, currentOrganization?.settings?.operationTiming?.shipping),
                btnAction: async () => {
                    await completeOrder({
                        order: order as any,
                        organizationId: organizationId as string,
                        orderId: order?.id,
                    });
                },
            }
        ].filter((step) => step.flows.includes(order?.flow as string));
    }, [order, currentOrganization?.settings?.operationTiming, organizationId, updateOrderStatus, completeOrder, requiresStripePayment, isStripeOrder, isOrderPaid, preparationStartTime]);

    const activeStep = useMemo(() => {
        return steps.findIndex((s) => s.id === effectiveStatus) ?? 0;
    }, [effectiveStatus, steps]);

    const handleNext = async () => {
        try {
            await steps[activeStep].btnAction?.();
            openSnackbar("Tu orden ha sido movida a la siguiente etapa", "success");
        } catch (error) {
            console.error(error);
            openSnackbar("Ocurrió un error al mover tu orden", "error");
        }
    };

    return (
        <Stack spacing={3} p={2}>
            <Stack
                sx={{
                    backgroundColor: "#DDEAFD",
                    borderRadius: 1,
                    px: 2,
                    py: 1.5,
                }}
            >
                <Stepper activeStep={activeStep} alternativeLabel sx={{ width: "100%" }}>
                    {steps.map((step, stepIndex) => {
                        const stepProps: { completed?: boolean } = {};
                        if (order?.status === OrderStatus.COMPLETED) {
                            stepProps.completed = true;
                        }
                        const labelProps: {
                            optional?: React.ReactNode;
                        } = {};

                        return (
                            <Step
                                key={step.id}
                                {...stepProps}
                                sx={{
                                    "& .MuiStepLabel-iconContainer .Mui-completed": {
                                        color: step.color,
                                    },
                                }}
                            >
                                <Stack alignItems="center">
                                    <StepLabel {...labelProps}>{step.title}</StepLabel>
                                    {activeStep === stepIndex ? (
                                        <Box p={1} sx={{ width: '50%' }}>
                                            <LinearProgress />
                                        </Box>
                                    ) : step?.time ? (
                                        <Typography variant="caption">
                                            {step.time}
                                            {step?.estimatedTime ? `/ ${step.estimatedTime} min` : null}
                                        </Typography>
                                    ) : null}
                                </Stack>
                            </Step>
                        );
                    })}
                </Stepper>
            </Stack>
            {isStripeOrder && effectiveStatus !== OrderStatus.REVIEWING ? (
                <Alert severity={isOrderPaid ? "success" : "warning"}>
                    {isOrderPaid
                        ? "Pago con tarjeta completado por el cliente (Stripe)."
                        : "Pendiente de pago con tarjeta por parte del cliente."}
                </Alert>
            ) : null}
            {steps[activeStep] ? (
                <Stack spacing={2} py={2} alignItems="center">
                    <Typography variant="h3" textAlign="center">{steps[activeStep]?.tagline}</Typography>
                    {steps[activeStep].btnLabel ? (
                        <LoadingButton
                            size="large"
                            color="warning"
                            variant="contained"
                            onClick={handleNext}
                            loading={isUpdatingStatusMutation.isLoading}
                        >
                            {steps[activeStep].btnLabel}
                        </LoadingButton>
                    ) : null}
                </Stack>
            ) : null}
        </Stack>
    );
};

export default OrderStages;
